import { call, put, takeEvery, all, select } from "redux-saga/effects";
import DatakeyMetadataApiUtil from "src/utils/DatakeyMetadataApiUtil";
import * as Actions from "src/actions/DatakeyMetadataActions";
import ApplicationState from "src/model/ApplicationState";
import { 
    DatakeySearchFilters, 
    SearchDatakeysResponse,
    CreateDatakeyResponse,
    UpdateDatakeyResponse,
    DeleteDatakeyResponse,
    GetDatakeyResponse,
    CreateDatakeyMultiMarketplaceResponse
} from "src/model/DatakeyMetadata";

const selectCurrentSearchFilters = (state: ApplicationState): DatakeySearchFilters => 
    state.datakeyMetadataRes.currentSearchFilters;

function* searchDatakeysSaga(action: any): Generator<any, void, unknown> {
    try {
        const validationError = DatakeyMetadataApiUtil.validateSearchFilters({
            feeType: action.payload.feeType,
            datakeyName: action.payload.datakeyName,
            marketplace: action.payload.marketplace
        });

        if (validationError) {
            yield put(Actions.searchDatakeysError(validationError));
            return;
        }

        const response = yield call(DatakeyMetadataApiUtil.searchDatakeys, {
            feeType: action.payload.feeType,
            datakeyName: action.payload.datakeyName,
            marketplace: action.payload.marketplace,
            limit: 50
        });
        
        yield put(Actions.searchDatakeysSuccess(response as SearchDatakeysResponse));
    } catch (error: any) {
        console.error('Search datakeys error:', error);
        const errorMessage = error.message || error.toString() || 'Unknown error';
        yield put(Actions.searchDatakeysError(`Failed to search datakeys: ${errorMessage}`));
    }
}

function* datakeyOperationSaga(
    action: Actions.CreateDatakeyRequestAction | Actions.UpdateDatakeyRequestAction | Actions.DeleteDatakeyRequestAction
): Generator<any, void, any> {
    try {
        let response;
        let operationType: 'create' | 'update' | 'delete';
        
        // Determine operation type and call appropriate API
        if (action.type === Actions.CREATE_DATAKEY_REQUEST) {
            response = yield call(DatakeyMetadataApiUtil.createDatakey, action.payload);
            operationType = 'create';
            yield put(Actions.createDatakeySuccess(response as CreateDatakeyResponse));
        } else if (action.type === Actions.UPDATE_DATAKEY_REQUEST) {
            response = yield call(DatakeyMetadataApiUtil.updateDatakey, action.payload);
            operationType = 'update';
            yield put(Actions.updateDatakeySuccess(response as UpdateDatakeyResponse));
        } else {
            response = yield call(DatakeyMetadataApiUtil.deleteDatakey, action.payload);
            operationType = 'delete';
            yield put(Actions.deleteDatakeySuccess(response as DeleteDatakeyResponse));
        }
        
        yield put(Actions.setOperationResult({
            type: operationType,
            success: true,
            message: `Successfully ${operationType}d datakey "${action.payload.datakeyName}" ` + 
                `from ${action.payload.publicFeeType} in ${action.payload.marketplace}`,
            datakeyName: action.payload.datakeyName
        }));
        
        // Use the target fee type and datakey name from the operation as search filters
        const newSearchFilters: DatakeySearchFilters = {
            feeType: action.payload.publicFeeType,
            datakeyName: action.payload.datakeyName,
            marketplace: action.payload.marketplace
        };
        yield put(Actions.searchDatakeysRequest(newSearchFilters));
    } catch (error: any) {
        console.error('Datakey operation error:', error);
        const errorMessage = error.message || error.toString() || 'Unknown error';
        
        // Determine operation type for error handling
        let operationType: 'create' | 'update' | 'delete';
        if (action.type === Actions.CREATE_DATAKEY_REQUEST) {
            operationType = 'create';
            yield put(Actions.createDatakeyError(`Failed to create datakey: ${errorMessage}`));
        } else if (action.type === Actions.UPDATE_DATAKEY_REQUEST) {
            operationType = 'update';
            yield put(Actions.updateDatakeyError(`Failed to update datakey: ${errorMessage}`));
        } else {
            operationType = 'delete';
            yield put(Actions.deleteDatakeyError(`Failed to delete datakey: ${errorMessage}`));
        }
        
        yield put(Actions.setOperationResult({
            type: operationType,
            success: false,
            message: `Failed to ${operationType} datakey "${action.payload.datakeyName}": ${errorMessage}`,
            datakeyName: action.payload.datakeyName
        }));
    }
}

function* createDatakeyMultiSaga(action: any): Generator<any, void, any> {
    try {
        const response = yield call(DatakeyMetadataApiUtil.createDatakeyMultiMarketplace, action.payload);
        yield put(Actions.createDatakeyMultiSuccess(response as CreateDatakeyMultiMarketplaceResponse));
        
        // Use the target fee type and datakey name from the operation as search filters
        const newSearchFilters: DatakeySearchFilters = {
            feeType: action.payload.publicFeeType,
            datakeyName: action.payload.datakeyName,
            marketplace: undefined // Clear marketplace to search across all marketplaces
        };
        yield put(Actions.searchDatakeysRequest(newSearchFilters));
    } catch (error: any) {
        console.error('Create multi-marketplace datakey error:', error);
        const errorMessage = error.message || error.toString() || 'Unknown error';
        yield put(Actions.createDatakeyMultiError(`Failed to create datakey: ${errorMessage}`));
        
        yield put(Actions.setOperationResult({
            type: 'create',
            success: false,
            message: `Failed to create datakey "${action.payload.datakeyName}" in multiple marketplaces: ${errorMessage}`,
            datakeyName: action.payload.datakeyName
        }));
    }
}


function* preFillFromExistingSaga(action: any): Generator<any, void, any> {
    try {
        const response = yield call(DatakeyMetadataApiUtil.getDatakey, action.payload);
        yield put(Actions.preFillFromExistingSuccess(response as GetDatakeyResponse));
    } catch (error: any) {
        console.error('Pre-fill from existing error:', error);
        const errorMessage = error.message || error.toString() || 'Unknown error';
        yield put(Actions.preFillFromExistingError(`Failed to load datakey for pre-fill: ${errorMessage}`));
    }
}

function* loadMoreResultsSaga(action: any): Generator<any, void, any> {
    try {
        const response = yield call(DatakeyMetadataApiUtil.searchDatakeys, {
            feeType: action.payload.feeType,
            datakeyName: action.payload.datakeyName,
            marketplace: action.payload.marketplace,
            limit: 50,
            nextToken: action.payload.nextToken
        });
        
        yield put(Actions.loadMoreResultsSuccess(response as SearchDatakeysResponse));
    } catch (error: any) {
        console.error('Load more results error:', error);
        const errorMessage = error.message || error.toString() || 'Unknown error';
        yield put(Actions.loadMoreResultsError(`Failed to load more results: ${errorMessage}`));
    }
}

export default function* datakeyMetadataSagas(): Generator<any, void, any> {
    yield all([
        takeEvery(Actions.SEARCH_DATAKEYS_REQUEST, searchDatakeysSaga),
        takeEvery(Actions.LOAD_MORE_RESULTS_REQUEST, loadMoreResultsSaga),
        takeEvery(Actions.CREATE_DATAKEY_REQUEST, datakeyOperationSaga),
        takeEvery(Actions.UPDATE_DATAKEY_REQUEST, datakeyOperationSaga),
        takeEvery(Actions.DELETE_DATAKEY_REQUEST, datakeyOperationSaga),
        takeEvery(Actions.CREATE_DATAKEY_MULTI_REQUEST, createDatakeyMultiSaga),
        takeEvery(Actions.PREFILL_FROM_EXISTING_REQUEST, preFillFromExistingSaga),
    ]);
}
